import { useState, useCallback } from "react";
import apiClient from "../../services/apiClient";

export const useAssignmentState = (
  entityHandler,
  config,
  assignableItemsApi
) => {
  const [selectedAssignableItems, setSelectedAssignableItems] = useState(
    new Set()
  );
  const [originalSelectedItems, setOriginalSelectedItems] = useState(new Set());

  // Helper function to compare two Sets
  const areSetsEqual = useCallback((set1, set2) => {
    if (set1.size !== set2.size) return false;
    for (let item of set1) {
      if (!set2.has(item)) return false;
    }
    return true;
  }, []);

  // Check if assignments have changed
  const areAssignmentsChanged = useCallback(() => {
    return !areSetsEqual(selectedAssignableItems, originalSelectedItems);
  }, [selectedAssignableItems, originalSelectedItems, areSetsEqual]);

  // Fetch assignable items for a specific item
  const fetchAssignableItemsForItem = useCallback(
    async (itemId) => {
      try {
        const endpoint = entityHandler.getAssignmentsURL(itemId);
        const res = await apiClient.get(endpoint);

        if (res.data) {
          const itemSet = new Set(res.data?.map((id) => Number(id)));
          setSelectedAssignableItems(itemSet);
          setOriginalSelectedItems(new Set(itemSet));
        }
      } catch (error) {
        console.error(
          `Failed to fetch assigned ${config.assignableItemLabel.toLowerCase()} for ${config.itemLabel.toLowerCase()} ${itemId}:`,
          error
        );
        // Optional: Handle error state
        // setError(error.message);
      }
    },
    [entityHandler, config] // Removed assignableItemsApi from dependencies
  );

  // Toggle an assignable item
  const toggleAssignableItem = useCallback(
    (itemId) => {
      const newSet = new Set(selectedAssignableItems);
      const wasChecked = newSet.has(itemId);

      if (wasChecked) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }

      setSelectedAssignableItems(newSet);
    },
    [selectedAssignableItems]
  );

  // Reset assignments to original state
  const resetAssignments = useCallback(() => {
    setSelectedAssignableItems(new Set(originalSelectedItems));
  }, [originalSelectedItems]);

  // Mark assignments as saved
  const markAssignmentsSaved = useCallback(() => {
    setOriginalSelectedItems(new Set(selectedAssignableItems));
  }, [selectedAssignableItems]);

  // Save assignment changes to API
  const saveAssignmentChanges = useCallback(
    async (selectedItem, entityHandler) => {
      const currentIds = selectedAssignableItems;
      const originalIds = originalSelectedItems;
      const additions = [...currentIds].filter((id) => !originalIds.has(id));
      const removals = [...originalIds].filter((id) => !currentIds.has(id));

      // Process additions
      for (const assignableItemId of additions) {
        const url = entityHandler.addAssignmentURL(
          selectedItem.id,
          assignableItemId
        );
        await apiClient.put(url);
      }

      // Process removals
      for (const assignableItemId of removals) {
        const url = entityHandler.removeAssignmentURL(
          selectedItem.id,
          assignableItemId
        );
        await apiClient.put(url);
      }
    },
    [selectedAssignableItems, originalSelectedItems]
  );

  return {
    selectedAssignableItems,
    originalSelectedItems,
    fetchAssignableItemsForItem,
    toggleAssignableItem,
    areAssignmentsChanged,
    resetAssignments,
    markAssignmentsSaved,
    saveAssignmentChanges,
  };
};
